"use client";

import { Laptop, Puzzle, X } from "lucide-react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { buttonClass } from "@/components/ui/button";
import { useConsent } from "@/lib/consent";
import {
	detectExtensionBrowserSupport,
	EXTENSION_STORE_URL,
	type ExtensionBrowserSupport,
} from "@/lib/extension/browser";
import { requestSendToLaptop } from "@/lib/extension/send-to-laptop-client";
import { isPromptSnoozed, snoozePrompt } from "@/lib/extension/snooze";
import { useExtensionInstalled } from "@/lib/extension/use-extension-installed";
import { isJobDetailPath } from "@/lib/jobs/is-job-detail-path";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";

// Holds a snooze-until timestamp (ms). The legacy value "1" (dismissed
// forever) is read as expired, see src/lib/extension/snooze.ts.
const STORAGE_KEY = "ld-ext-banner-dismissed";

type Variant = "install" | "send_to_laptop";

// Tags shown/clicked events so before/after copy can be compared in PostHog.
// The install copy claims only what scripts/research/apply-benchmark-2026-10
// measured (Greenhouse/Lever/Ashby: 64-76% of required fields filled).
const COPY_VERSION = "benchmark-2026-10";
type SendState = "idle" | "sending" | "sent" | "signin" | "error";

export function ExtensionBanner({ locale }: { locale: string }) {
	const [support, setSupport] = useState<ExtensionBrowserSupport | null>(null);
	const [snoozed, setSnoozed] = useState(true);
	const [sendState, setSendState] = useState<SendState>("idle");
	const { record: consentRecord } = useConsent();
	const posthog = usePostHog();
	const footerClaimed = useStickyFooterClaimed();
	const installed = useExtensionInstalled();
	const pathname = usePathname();

	useEffect(() => {
		setSupport(detectExtensionBrowserSupport());
		setSnoozed(isPromptSnoozed(STORAGE_KEY));
	}, []);

	// Desktop Chromium waits for the install check to settle as "not
	// installed", so people who already have the extension never see an
	// install prompt. Phones cannot run it, so there is nothing to wait for.
	const variant: Variant | null =
		support === "installable" && installed === false
			? "install"
			: support === "mobile"
				? "send_to_laptop"
				: null;
	const eligible = variant !== null && !snoozed;

	// Never stack on top of the cookie-consent banner — wait for consent to
	// resolve (accept/reject/save) before showing. Once it resolves, this
	// naturally re-renders (useConsent listens for the consent-changed event)
	// and the banner appears right after, no extra wiring needed.
	// ...and yield the bottom slot entirely when a page has claimed it
	// (useClaimStickyFooter). Only the post-purchase screen claims today, where
	// PR #4252 established that ambient units step aside — an install promo
	// pinned under someone's receipt is the same collision that fix addressed,
	// and this banner renders at the identical `fixed bottom-0 z-40` as the
	// affiliate bar that already honours the claim (mobile-sticky-ad.tsx:44).
	// Folded into `show` so the impression event below stays truthful.
	// Job detail pages are excluded: the inline ExtensionInstallNudge inside
	// JobApplyCta is the install surface there, and a second overlay stacked on
	// the Apply card was one of several seen in replays (scan 01a12825).
	const show =
		eligible &&
		consentRecord !== null &&
		!footerClaimed &&
		!isJobDetailPath(pathname);

	// Impression — the install-funnel top was previously unmeasured, so we had no
	// idea how many Chrome users even saw the install CTA. Fire once when shown.
	useEffect(() => {
		if (show)
			posthog?.capture("extension_install_banner_shown", {
				locale,
				variant,
				copy_version: COPY_VERSION,
			});
	}, [show, posthog, locale, variant]);

	function dismiss() {
		posthog?.capture("extension_install_banner_dismissed", {
			locale,
			variant,
		});
		snoozePrompt(STORAGE_KEY);
		setSnoozed(true);
	}

	async function sendToLaptop() {
		posthog?.capture("extension_install_banner_clicked", {
			locale,
			variant: "send_to_laptop",
			copy_version: COPY_VERSION,
		});
		setSendState("sending");
		const outcome: SendState = await requestSendToLaptop(locale);
		setSendState(outcome);
		posthog?.capture("extension_send_to_laptop_result", { locale, outcome });
	}

	if (!show) return null;

	const isEn = locale === "en";
	const loginHref =
		typeof window === "undefined"
			? `/${locale}/login`
			: `/${locale}/login?callbackURL=${encodeURIComponent(
					`${window.location.pathname}${window.location.search}`,
				)}`;

	return (
		// Arbitrates the slot via useStickyFooterClaimed above; the previous
		// @sticky-slot-ok escape is no longer the reason this passes check:sticky-slot.
		// Sits on top of the bottom ad bar (--ad-bar-h, 0px when none) instead of
		// under it — both are fixed z-40 and the bar now shows on desktop too.
		<div className="fixed bottom-[var(--ad-bar-h,0px)] left-0 right-0 z-40 border-t border-border/40 bg-background/95 backdrop-blur-sm shadow-sm">
			<div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-2.5">
				{variant === "install" ? (
					<div className="flex min-w-0 items-center gap-2 text-sm">
						<Puzzle
							className="h-4 w-4 shrink-0 text-primary"
							aria-hidden="true"
						/>
						<div className="flex min-w-0 flex-col">
							<span className="font-medium text-foreground">
								{isEn
									? "Auto-fill job applications on Greenhouse, Lever and Ashby"
									: "Isi formulir lamaran otomatis di Greenhouse, Lever, dan Ashby"}
							</span>
							<span className="hidden text-xs text-muted-foreground sm:inline">
								{isEn
									? "Our free extension fills about 2 of 3 required fields. You check, then send it yourself."
									: "Ekstensi gratis kami mengisi sekitar 2 dari 3 kolom wajib. Kamu cek, lalu kirim sendiri."}
							</span>
						</div>
					</div>
				) : (
					<div className="flex min-w-0 items-center gap-2 text-sm">
						<Laptop
							className="h-4 w-4 shrink-0 text-primary"
							aria-hidden="true"
						/>
						<span
							className="text-foreground"
							aria-live="polite"
							data-testid="extension-banner-laptop-status"
						>
							{sendState === "sent"
								? isEn
									? "Sent. Open the email on your laptop."
									: "Terkirim. Buka email-nya di laptop."
								: sendState === "signin"
									? isEn
										? "Sign in so we know where to send it."
										: "Masuk dulu supaya kami tahu ke mana mengirimnya."
									: sendState === "error"
										? isEn
											? "Could not send. Try again."
											: "Gagal mengirim. Coba lagi."
										: isEn
											? "The form-filling extension runs on a laptop."
											: "Ekstensi pengisi lamaran berjalan di laptop."}
						</span>
					</div>
				)}
				<div className="flex shrink-0 items-center gap-2">
					{variant === "install" ? (
						<a
							href={EXTENSION_STORE_URL}
							target="_blank"
							rel="noopener noreferrer"
							onClick={() =>
								posthog?.capture("extension_install_banner_clicked", {
									locale,
									variant: "install",
									copy_version: COPY_VERSION,
								})
							}
							className={buttonClass({ size: "xs" })}
						>
							{isEn ? "Add the free extension" : "Pasang ekstensi gratis"}
						</a>
					) : sendState === "signin" ? (
						<a
							href={loginHref}
							onClick={() =>
								posthog?.capture("extension_send_to_laptop_signin_clicked", {
									locale,
								})
							}
							className={buttonClass({ size: "xs" })}
						>
							{isEn ? "Sign in" : "Masuk"}
						</a>
					) : sendState === "sent" ? null : (
						<button
							type="button"
							onClick={sendToLaptop}
							disabled={sendState === "sending"}
							className={buttonClass({ size: "xs" })}
						>
							{sendState === "sending"
								? isEn
									? "Sending…"
									: "Mengirim…"
								: isEn
									? "Send to my laptop"
									: "Kirim ke laptop"}
						</button>
					)}
					<button
						type="button"
						onClick={dismiss}
						aria-label={
							isEn ? "Dismiss extension banner" : "Tutup banner ekstensi"
						}
						className="rounded-lg p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
					>
						<X className="h-4 w-4" />
					</button>
				</div>
			</div>
		</div>
	);
}
